import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  ArrowLeft, ArrowRight, CheckCircle, CreditCard, Building2, 
  User, MapPin, Phone, Mail, Package, PoundSterling, FileText,
  Shield, Calendar, Star, Crown, Clock, Plus, Info
} from 'lucide-react';
import axios from 'axios';
import PaymentProcessor from './PaymentProcessor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RetailerOnboardingFlow = ({ isOpen, onClose, selectedPackage, user }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Business Information
    business_name: '',
    email_address: '',
    address: '',
    postcode: '',
    main_contact: '',
    mobile_phone: '',
    
    // Business Details
    services_offered: '',
    products_offered: '',
    target_audience: '',
    special_instructions: '',
    discount_details: '',
    
    // Product/Service List (up to 10 items)
    discount_applicable_items: [''],
    
    // Minimum expenditure to activate discount
    minimum_expenditure: '',
    
    // Dynamic Discount System
    discount_type: 'flat', // 'flat' or 'percentage'
    
    // Flat Monetary Discount
    flat_discount_amount: '',
    
    // Category-Specific Percentage Discounts
    category_discounts: {
      students: '',
      pensioners: '',
      government_workers: '',
      nhs_key_workers: '',
      general_public: ''
    },
    
    // Package & Payment
    selected_package: selectedPackage || 'standard',
    accept_terms: false,
    
    // Generated fields
    generated_url: '',
    setup_fee: 35,
    monthly_fee: 15
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const packageDetails = {
    standard: {
      name: 'Standard',
      monthly: 15,
      setup: 35,
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'bg-blue-500',
      features: [
        'Basic business listing',
        'Contact information display',
        'Basic search visibility',
        'Customer reviews',
        'Email support'
      ]
    },
    deluxe: {
      name: 'Deluxe',
      monthly: 25,
      setup: 35,
      icon: <Star className="h-6 w-6" />,
      color: 'bg-orange',
      features: [
        'Everything in Standard',
        '3 updates per month',
        'Enhanced business profile',
        'Priority customer support',
        'Advanced analytics'
      ]
    },
    platinum: {
      name: 'Platinum',
      monthly: 35,
      setup: 35,
      icon: <Crown className="h-6 w-6" />,
      color: 'bg-amber',
      features: [
        'Everything in Deluxe',
        '6 updates per month',
        'Premium listing placement',
        'Featured in newsletters',
        'Dedicated account manager'
      ]
    },
    elite: {
      name: 'Elite/Sponsor',
      monthly: 'Request Quote',
      setup: 'Custom',
      icon: <Crown className="h-6 w-6" />,
      color: 'bg-purple-600',
      isRequestQuote: true,
      features: [
        'Everything in Platinum',
        'Unlimited updates',
        'Homepage banner placement',
        'Custom integration solutions',
        'Exclusive partnership benefits',
        'Dedicated account management',
        'Custom pricing & packages'
      ]
    }
  };

  const steps = [
    { id: 1, title: 'Package Selection', icon: <Package className="h-5 w-5" /> },
    { id: 2, title: 'Business Information', icon: <Building2 className="h-5 w-5" /> },
    { id: 3, title: 'Business Details', icon: <FileText className="h-5 w-5" /> },
    { id: 4, title: 'Review & Confirm', icon: <CheckCircle className="h-5 w-5" /> },
    { id: 5, title: 'Payment', icon: <CreditCard className="h-5 w-5" /> },
    { id: 6, title: 'Confirmation', icon: <CheckCircle className="h-5 w-5" /> }
  ];

  useEffect(() => {
    if (selectedPackage && packageDetails[selectedPackage]) {
      const pkg = packageDetails[selectedPackage];
      setFormData(prev => ({
        ...prev,
        selected_package: selectedPackage,
        setup_fee: pkg.setup,
        monthly_fee: pkg.monthly,
        generated_url: `https://${prev.business_name.toLowerCase().replace(/[^a-z0-9]/g, '-')}.shoppersdiscount.club`
      }));
    }
  }, [selectedPackage]);

  useEffect(() => {
    if (formData.business_name) {
      const urlFriendlyName = formData.business_name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      setFormData(prev => ({
        ...prev,
        generated_url: `https://${urlFriendlyName}.shoppersdiscount.club`
      }));
    }
  }, [formData.business_name]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper functions for managing discount applicable items
  const addDiscountItem = () => {
    if (formData.discount_applicable_items.length < 10) {
      setFormData(prev => ({
        ...prev,
        discount_applicable_items: [...prev.discount_applicable_items, '']
      }));
    }
  };

  const removeDiscountItem = (index) => {
    if (formData.discount_applicable_items.length > 1) {
      setFormData(prev => ({
        ...prev,
        discount_applicable_items: prev.discount_applicable_items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateDiscountItem = (index, value) => {
    setFormData(prev => ({
      ...prev,
      discount_applicable_items: prev.discount_applicable_items.map((item, i) => 
        i === index ? value : item
      )
    }));
  };

  // Discount system helper functions
  const updateCategoryDiscount = (category, value) => {
    // Validate percentage (0-50%)
    const numValue = parseFloat(value);
    if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 50)) {
      return; // Invalid value, don't update
    }
    
    setFormData(prev => ({
      ...prev,
      category_discounts: {
        ...prev.category_discounts,
        [category]: value
      }
    }));
  };

  const updateFlatDiscount = (value) => {
    // Validate amount (0-100)
    const numValue = parseFloat(value);
    if (value !== '' && (isNaN(numValue) || numValue < 0 || numValue > 100)) {
      return; // Invalid value, don't update
    }
    
    setFormData(prev => ({
      ...prev,
      flat_discount_amount: value
    }));
  };

  const generateDiscountPreview = () => {
    if (formData.discount_type === 'flat') {
      if (formData.flat_discount_amount) {
        return `Great! You're offering £${formData.flat_discount_amount} off all purchases — every customer, every day! 🎉`;
      }
      return 'Add your discount amount to see the preview';
    } else {
      const activeCategories = Object.entries(formData.category_discounts)
        .filter(([_, value]) => value && value > 0)
        .map(([category, value]) => {
          const categoryNames = {
            students: 'Students',
            pensioners: 'Pensioners', 
            government_workers: 'Government Workers',
            nhs_key_workers: 'NHS/Key Workers',
            general_public: 'General Public'
          };
          return `${value}% for ${categoryNames[category]}`;
        });
      
      if (activeCategories.length > 0) {
        const maxDiscount = Math.max(...Object.values(formData.category_discounts).filter(v => v).map(v => parseInt(v)));
        return `Excellent! You're rewarding ${activeCategories.join(', ')} with exclusive savings up to ${maxDiscount}% off! 🎯`;
      }
      return 'Add discounts for your chosen groups to see the preview';
    }
  };

  const validateDiscounts = () => {
    if (formData.discount_type === 'flat') {
      return formData.flat_discount_amount && parseFloat(formData.flat_discount_amount) > 0;
    } else {
      return Object.values(formData.category_discounts).some(value => value && parseFloat(value) > 0);
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.selected_package;
      case 2:
        return formData.business_name && formData.email_address && formData.address && 
               formData.postcode && formData.main_contact && formData.mobile_phone;
      case 3:
        // Check required fields
        const hasRequiredFields = formData.services_offered && formData.products_offered;
        
        // Check discount validation - at least one discount must be specified
        const hasValidDiscounts = validateDiscounts();
        
        return hasRequiredFields && hasValidDiscounts;
      case 4:
        return formData.accept_terms;
      default:
        return true;
    }
  };

  const handleEliteQuote = () => {
    // Generate email content for Elite/Sponsor package request
    const emailSubject = `Elite/Sponsor Package Request - ${formData.business_name}`;
    const emailBody = `Dear Shoppers Discount Club Team,

I would like to request a quote for the Elite/Sponsor package.

Business Details:
- Business Name: ${formData.business_name}
- Contact Person: ${formData.main_contact}
- Email: ${formData.email_address}
- Phone: ${formData.mobile_phone}
- Address: ${formData.address}, ${formData.postcode}

Services/Products:
- Services Offered: ${formData.services_offered}
- Products Offered: ${formData.products_offered}
- Target Audience: ${formData.target_audience}

Special Requirements:
${formData.special_instructions}

Discount Details:
${formData.discount_details}

Please provide a custom quote for our Elite/Sponsor package requirements.

Best regards,
${formData.main_contact}`;

    // Open email client
    const emailLink = `mailto:order@shoppersdiscount.club?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(emailLink, '_blank');
    
    // Show confirmation
    setCurrentStep(6);
    setConfirmation({
      applicationId: 'elite-quote-request',
      businessName: formData.business_name,
      package: { name: 'Elite/Sponsor Quote Request' },
      isEliteQuote: true
    });
  };

  const nextStep = () => {
    if (validateStep()) {
      if (currentStep === 4) {
        // Check if Elite/Sponsor package - handle differently
        if (formData.selected_package === 'elite') {
          handleEliteQuote();
        } else {
          // Submit application before going to payment
          submitApplication();
        }
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const submitApplication = async () => {
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(`${API}/business-applications`, {
        ...formData,
        application_status: 'pending_payment'
      }, { headers });

      setApplicationId(response.data.id);
      setCurrentStep(5); // Go to payment step
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentResult) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Update application with payment details
      await axios.put(`${API}/business-applications/${applicationId}/payment`, {
        payment_status: 'completed',
        transaction_id: paymentResult.transactionId,
        payment_method: paymentResult.method,
        amount_paid: formData.setup_fee + formData.monthly_fee
      }, { headers });

      setConfirmation({
        applicationId,
        businessName: formData.business_name,
        package: packageDetails[formData.selected_package],
        chatbotUrl: formData.generated_url,
        transactionId: paymentResult.transactionId
      });
      
      setCurrentStep(6); // Go to confirmation
    } catch (error) {
      console.error('Error processing payment confirmation:', error);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Choose Your Package</h3>
              <p className="text-gray-600">Select the perfect plan for your business needs</p>
            </div>

            <div className="space-y-4">
              {Object.entries(packageDetails).map(([key, pkg]) => (
                <Card 
                  key={key}
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.selected_package === key 
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleInputChange('selected_package', key)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`${pkg.color} p-3 rounded-lg text-white`}>
                          {pkg.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-charcoal">{pkg.name}</h4>
                          <p className="text-sm text-gray-600">{pkg.features[0]}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {pkg.isRequestQuote ? (
                          <>
                            <div className="text-2xl font-bold text-purple-600">Request Quote</div>
                            <div className="text-sm text-gray-600">Custom pricing</div>
                            <div className="text-xs text-gray-500">Bespoke solutions</div>
                          </>
                        ) : (
                          <>
                            <div className="text-2xl font-bold text-charcoal">£{pkg.monthly}</div>
                            <div className="text-sm text-gray-600">/month</div>
                            <div className="text-xs text-gray-500">Setup: £{pkg.setup}</div>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Business Information</h3>
              <p className="text-gray-600">Tell us about your business</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  placeholder="Your business name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email_address}
                  onChange={(e) => handleInputChange('email_address', e.target.value)}
                  placeholder="business@example.com"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Business Address *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Full business address"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Postcode *</Label>
                <Input
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  placeholder="Postcode"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Main Contact Name *</Label>
                <Input
                  value={formData.main_contact}
                  onChange={(e) => handleInputChange('main_contact', e.target.value)}
                  placeholder="Contact person name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Mobile Phone *</Label>
                <Input
                  value={formData.mobile_phone}
                  onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                  placeholder="Phone number"
                  className="mt-1"
                />
              </div>
            </div>

            {formData.generated_url && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <Label className="text-green-800 font-medium">Your Unique Chatbot URL</Label>
                <div className="mt-1 text-green-700 font-mono text-sm">
                  {formData.generated_url}
                </div>
                <p className="text-green-600 text-xs mt-1">
                  This will be your dedicated chatbot and offers page
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Business Details</h3>
              <p className="text-gray-600">Help customers find what they need</p>
            </div>

            <div>
              <Label>Type of Services *</Label>
              <Textarea
                value={formData.services_offered}
                onChange={(e) => handleInputChange('services_offered', e.target.value)}
                placeholder="Describe the services you offer..."
                rows={3}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Type of Products *</Label>
              <Textarea
                value={formData.products_offered}
                onChange={(e) => handleInputChange('products_offered', e.target.value)}
                placeholder="Describe the products you sell..."
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Specific Products/Services List */}
            <div>
              <Label className="text-base font-semibold text-charcoal">Specific Products/Services for Discounts</Label>
              <p className="text-sm text-gray-600 mb-3">List up to 10 specific products or services that your discounts apply to (optional but recommended)</p>
              
              <div className="space-y-2">
                {formData.discount_applicable_items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={item}
                      onChange={(e) => updateDiscountItem(index, e.target.value)}
                      placeholder={`Product/Service ${index + 1}`}
                      className="flex-1"
                    />
                    {formData.discount_applicable_items.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeDiscountItem(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              {formData.discount_applicable_items.length < 10 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addDiscountItem}
                  className="mt-2 text-primary border-primary hover:bg-primary hover:text-white"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Product/Service ({formData.discount_applicable_items.length}/10)
                </Button>
              )}
            </div>

            {/* Minimum Expenditure */}
            <div>
              <Label className="text-base font-semibold text-charcoal">Minimum Expenditure to Activate Discount</Label>
              <p className="text-sm text-gray-600 mb-2">Set the minimum purchase amount required to qualify for discounts (optional)</p>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-gray-700">£</span>
                <Input
                  type="number"
                  value={formData.minimum_expenditure}
                  onChange={(e) => handleInputChange('minimum_expenditure', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="max-w-xs"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Leave blank for no minimum requirement</p>
            </div>

            <div>
              <Label>Target Audience</Label>
              <Input
                value={formData.target_audience}
                onChange={(e) => handleInputChange('target_audience', e.target.value)}
                placeholder="Who are your ideal customers?"
                className="mt-1"
              />
            </div>

            {/* Dynamic Discount System */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Label className="text-lg font-semibold text-charcoal">How would you like to reward shoppers?</Label>
                <div className="flex items-center gap-1 text-primary">
                  <Info className="h-4 w-4" />
                  <span className="text-xs">Choose one option — you can always edit your discounts later in your dashboard.</span>
                </div>
              </div>
              
              {/* Discount Type Selector */}
              <div className="mb-6">
                <Label className="text-base font-medium text-charcoal mb-3 block">Choose Your Discount Type:</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Card 
                    className={`cursor-pointer transition-all duration-200 ${
                      formData.discount_type === 'flat' 
                        ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleInputChange('discount_type', 'flat')}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full border-2 border-primary flex items-center justify-center">
                          {formData.discount_type === 'flat' && (
                            <div className="w-2 h-2 rounded-full bg-primary"></div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-charcoal">💷 Offer a flat £ discount for everyone</h4>
                          <p className="text-sm text-gray-600">Same great savings for all shoppers</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card 
                    className={`cursor-pointer transition-all duration-200 ${
                      formData.discount_type === 'percentage' 
                        ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleInputChange('discount_type', 'percentage')}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full border-2 border-primary flex items-center justify-center">
                          {formData.discount_type === 'percentage' && (
                            <div className="w-2 h-2 rounded-full bg-primary"></div>
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-charcoal">% Offer different discounts for specific shopper groups</h4>
                          <p className="text-sm text-gray-600">Tailor savings for your community</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Dynamic Fields Based on Selection */}
              {formData.discount_type === 'flat' ? (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <Label className="text-base font-semibold text-charcoal mb-2 block">
                    Enter your flat discount amount (£)
                  </Label>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl font-bold text-gray-700">£</span>
                    <Input
                      type="number"
                      value={formData.flat_discount_amount}
                      onChange={(e) => updateFlatDiscount(e.target.value)}
                      placeholder="e.g. £5.00"
                      min="0"
                      max="100"
                      step="0.01"
                      className="max-w-xs text-lg"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Every shopper gets this discount — great for building loyalty! Maximum £100.
                  </p>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <Label className="text-base font-semibold text-charcoal mb-3 block">
                    Enter discount percentage for each group (max 50%)
                  </Label>
                  <p className="text-sm text-gray-600 mb-4">Leave blank for any groups you don't want to include — you're in control!</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries({
                      students: 'Students',
                      pensioners: 'Pensioners', 
                      government_workers: 'Government Workers',
                      nhs_key_workers: 'NHS/Key Workers',
                      general_public: 'General Public'
                    }).map(([key, label]) => (
                      <div key={key} className="flex items-center gap-3">
                        <Label className="min-w-0 flex-1 font-medium text-charcoal">{label}</Label>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={formData.category_discounts[key]}
                            onChange={(e) => updateCategoryDiscount(key, e.target.value)}
                            placeholder="e.g. 15"
                            min="0"
                            max="50"
                            step="1"
                            className="w-20 text-center"
                          />
                          <span className="text-gray-600 font-medium">%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Discount Preview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <Label className="text-sm font-semibold text-blue-800 mb-1 block">Preview:</Label>
                <p className="text-blue-700 font-medium">{generateDiscountPreview()}</p>
              </div>
            </div>

            <div>
              <Label>Special Instructions (Optional)</Label>
              <Textarea
                value={formData.special_instructions}
                onChange={(e) => handleInputChange('special_instructions', e.target.value)}
                placeholder="Any additional information or special requirements..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
        );

      case 4:
        const currentPackage = packageDetails[formData.selected_package];
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Review & Confirm</h3>
              <p className="text-gray-600">Please review your details before proceeding to payment</p>
            </div>

            {/* Package Summary */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`${currentPackage.color} p-3 rounded-lg text-white`}>
                      {currentPackage.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-charcoal">{currentPackage.name} Package</h4>
                      <p className="text-sm text-gray-600">
                        {currentPackage.isRequestQuote ? 'Custom Quote Request' : 'Monthly Subscription'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {currentPackage.isRequestQuote ? (
                      <>
                        <div className="text-2xl font-bold text-purple-600">Request Quote</div>
                        <div className="text-sm text-gray-600">Custom pricing</div>
                      </>
                    ) : (
                      <>
                        <div className="text-2xl font-bold text-charcoal">£{currentPackage.monthly}/month</div>
                        <div className="text-sm text-gray-600">Setup Fee: £{currentPackage.setup}</div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><strong>Business:</strong> {formData.business_name}</div>
                  <div><strong>Email:</strong> {formData.email_address}</div>
                  <div><strong>Contact:</strong> {formData.main_contact}</div>
                  <div><strong>Phone:</strong> {formData.mobile_phone}</div>
                </div>
                <div className="text-sm">
                  <strong>Address:</strong> {formData.address}, {formData.postcode}
                </div>
                <div className="text-sm">
                  <strong>Chatbot URL:</strong> 
                  <span className="font-mono text-primary ml-2">{formData.generated_url}</span>
                </div>
              </CardContent>
            </Card>

            {/* Payment Summary */}
            {!currentPackage.isRequestQuote && (
              <Card className="border-orange/20 bg-orange/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PoundSterling className="h-5 w-5" />
                    Payment Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Setup Fee (One-time)</span>
                      <span>£{formData.setup_fee}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>First Month Subscription</span>
                      <span>£{formData.monthly_fee}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                      <span>Total Due Today</span>
                      <span>£{formData.setup_fee + formData.monthly_fee}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Elite Quote Request Info */}
            {currentPackage.isRequestQuote && (
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-700">
                    <Mail className="h-5 w-5" />
                    Quote Request Process
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Your details will be sent to order@shoppersdiscount.club</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Our team will prepare a custom quote within 24-48 hours</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>Invoice and package details will be handled manually</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span>No payment required at this stage</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Terms & Conditions */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="accept-terms"
                  checked={formData.accept_terms}
                  onCheckedChange={(checked) => handleInputChange('accept_terms', checked)}
                />
                <div className="text-sm">
                  <label htmlFor="accept-terms" className="cursor-pointer">
                    I agree to the{' '}
                    <a href="/terms-of-sale" target="_blank" className="text-primary hover:underline">
                      Refund & Cancellation Policy
                    </a>
                    {' '}and{' '}
                    <a href="/terms-of-use" target="_blank" className="text-primary hover:underline">
                      Terms of Use
                    </a>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-charcoal mb-2">Secure Payment</h3>
              <p className="text-gray-600">Complete your payment to activate your listing</p>
            </div>

            <PaymentProcessor
              amount={formData.setup_fee + formData.monthly_fee}
              orderDetails={{
                package: packageDetails[formData.selected_package].name,
                setupFee: `£${formData.setup_fee}`,
                monthlyFee: `£${formData.monthly_fee}`,
                businessName: formData.business_name
              }}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setCurrentStep(4)}
              customerEmail={formData.email_address}
            />
          </div>
        );

      case 6:
        return (
          <div className="text-center space-y-6">
            <div className={`${confirmation?.isEliteQuote ? 'bg-purple-100' : 'bg-green-100'} rounded-full w-24 h-24 flex items-center justify-center mx-auto`}>
              {confirmation?.isEliteQuote ? (
                <Mail className="h-12 w-12 text-purple-600" />
              ) : (
                <CheckCircle className="h-12 w-12 text-green-600" />
              )}
            </div>
            
            <div>
              <h3 className="text-3xl font-bold text-charcoal mb-4">
                {confirmation?.isEliteQuote ? 'Quote Request Sent!' : 'Welcome to Shoppers Discount Club!'}
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                {confirmation?.isEliteQuote ? (
                  <>Thank you! Your Elite/Sponsor package quote request has been received. We will review your details and get back to you soon.</>
                ) : (
                  <>Payment successful! Welcome to the community. You will receive your confirmation email shortly.</>
                )}
              </p>
            </div>

            <Card className={`${confirmation?.isEliteQuote ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
              <CardContent className="p-6">
                <h4 className="font-semibold text-charcoal mb-4">What happens next?</h4>
                <div className="text-left space-y-2 text-sm text-gray-700">
                  {confirmation?.isEliteQuote ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-purple-600" />
                        <span>Quote request sent to order@shoppersdiscount.club</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-orange-600" />
                        <span>Our team will review your requirements (24-48 hours)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span>Custom quote and package details will be prepared</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <PoundSterling className="h-4 w-4 text-green-600" />
                        <span>Invoice will be handled manually with bespoke pricing</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-purple-600" />
                        <span>Elite listing will be created after quote acceptance</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Payment confirmation email sent</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-orange-600" />
                        <span>Application marked as "Pending Approval"</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-blue-600" />
                        <span>Window stickers & QR code instructions will be dispatched</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-purple-600" />
                        <span>Admin team will review and approve your listing (24-48 hours)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-green-600" />
                        <span>You'll receive a "Your listing is now live" confirmation email</span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {confirmation && (
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-semibold text-charcoal mb-4">Your Details</h4>
                  <div className="text-sm space-y-2">
                    <div><strong>Application ID:</strong> {confirmation.applicationId}</div>
                    <div><strong>Business:</strong> {confirmation.businessName}</div>
                    <div><strong>Package:</strong> {confirmation.package.name}</div>
                    <div><strong>Chatbot URL:</strong> <span className="font-mono text-primary">{confirmation.chatbotUrl}</span></div>
                    <div><strong>Transaction ID:</strong> {confirmation.transactionId}</div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Button onClick={onClose} className="w-full bg-primary hover:bg-primary/90">
              Get Started! 
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Retailer Onboarding
          </DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  currentStep >= step.id 
                    ? 'bg-primary border-primary text-white' 
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    step.icon
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-2 transition-colors ${
                    currentStep > step.id ? 'bg-primary' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-center">
            <h3 className="font-medium text-charcoal">{steps[currentStep - 1]?.title}</h3>
            <p className="text-sm text-gray-600">Step {currentStep} of {steps.length}</p>
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 6 && (
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>

            <Button
              onClick={nextStep}
              disabled={!validateStep() || isSubmitting}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90"
            >
              {currentStep === 4 ? (
                isSubmitting ? 'Almost there...' : 
                (formData.selected_package === 'elite' ? 'Send Quote Request' : (
                  <>
                    Let's Go!
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </>
                ))
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RetailerOnboardingFlow;